import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { DollarSign, Calendar, TrendingUp, Info } from 'lucide-react';

const HomeLoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [loanType, setLoanType] = useState('PI'); // 'PI' or 'IO'
  const [ioPeriod, setIoPeriod] = useState(5); // Interest Only period in years

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateRepayments = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;

    if (loanType === 'PI') {
      // Principal & Interest calculation
      const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                            (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      
      const totalPayments = monthlyPayment * numberOfPayments;
      const totalInterest = totalPayments - principal;
      
      // Calculate first month breakdown
      const firstMonthInterest = principal * monthlyRate;
      const firstMonthPrincipal = monthlyPayment - firstMonthInterest;

      return {
        weekly: monthlyPayment * 12 / 52,
        fortnightly: monthlyPayment * 12 / 26,
        monthly: monthlyPayment,
        totalInterest,
        totalPayments,
        firstMonthInterest,
        firstMonthPrincipal,
        principalPaidFirstYear: calculateFirstYearPrincipal(monthlyPayment, monthlyRate, principal),
        isInterestOnly: false
      };
    } else {
      // Interest Only with P&I conversion
      const ioMonths = ioPeriod * 12;
      const piMonths = numberOfPayments - ioMonths;
      
      // Interest Only period
      const ioMonthlyPayment = principal * monthlyRate;
      const ioTotalInterest = ioMonthlyPayment * ioMonths;
      
      // P&I period (after IO)
      const piMonthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, piMonths)) / 
                              (Math.pow(1 + monthlyRate, piMonths) - 1);
      const piTotalPayments = piMonthlyPayment * piMonths;
      const piTotalInterest = piTotalPayments - principal;
      
      // Combined totals
      const totalInterest = ioTotalInterest + piTotalInterest;
      const totalPayments = ioTotalInterest + piTotalPayments;
      
      // First month breakdown (IO period)
      const firstMonthInterest = ioMonthlyPayment;
      const firstMonthPrincipal = 0;
      
      // First P&I payment breakdown
      const firstPIInterest = principal * monthlyRate;
      const firstPIPrincipal = piMonthlyPayment - firstPIInterest;

      return {
        weekly: ioMonthlyPayment * 12 / 52,
        fortnightly: ioMonthlyPayment * 12 / 26,
        monthly: ioMonthlyPayment,
        totalInterest,
        totalPayments,
        firstMonthInterest,
        firstMonthPrincipal,
        principalPaidFirstYear: 0,
        isInterestOnly: true,
        ioPeriod,
        ioMonthlyPayment,
        piMonthlyPayment,
        piPeriod: loanTerm - ioPeriod,
        piWeekly: piMonthlyPayment * 12 / 52,
        piFortnightly: piMonthlyPayment * 12 / 26,
        piMonthly: piMonthlyPayment,
        firstPIInterest,
        firstPIPrincipal,
        paymentIncrease: piMonthlyPayment - ioMonthlyPayment
      };
    }
  };

  const calculateFirstYearPrincipal = (monthlyPayment, monthlyRate, principal) => {
    let balance = principal;
    let totalPrincipal = 0;
    
    for (let i = 0; i < 12; i++) {
      const interest = balance * monthlyRate;
      const principalPayment = monthlyPayment - interest;
      totalPrincipal += principalPayment;
      balance -= principalPayment;
    }
    
    return totalPrincipal;
  };

  const results = calculateRepayments();

  return (
    <div>
      <Header />
      <div className="min-h-screen bg-page py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Home Loan Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Calculate your home loan repayments with Principal & Interest or Interest Only options
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Left Section - Inputs */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Loan Details</CardTitle>
                  <CardDescription>Enter your loan information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Loan Amount ($)</Label>
                    <Input 
                      type="number"
                      value={loanAmount || ''}
                      onChange={(e) => setLoanAmount(parseFloat(e.target.value) || 0)}
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <Label>Interest Rate (% p.a.)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={interestRate || ''}
                      onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <Label>Loan Term (years)</Label>
                    <Input 
                      type="number"
                      value={loanTerm || ''}
                      onChange={(e) => setLoanTerm(parseFloat(e.target.value) || 0)}
                      className="text-lg"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Loan Type</CardTitle>
                  <CardDescription>Select your repayment type</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant={loanType === 'PI' ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-4"
                    onClick={() => setLoanType('PI')}
                  >
                    <div className="text-left">
                      <div className="font-semibold">Principal & Interest</div>
                      <div className="text-xs mt-1 opacity-80">
                        Pay off principal and interest each payment
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant={loanType === 'IO' ? 'default' : 'outline'}
                    className="w-full justify-start h-auto py-4"
                    onClick={() => setLoanType('IO')}
                  >
                    <div className="text-left">
                      <div className="font-semibold">Interest Only → P&I</div>
                      <div className="text-xs mt-1 opacity-80">
                        IO period then converts to P&I
                      </div>
                    </div>
                  </Button>
                  
                  {loanType === 'IO' && (
                    <div className="pt-3 border-t">
                      <Label>Interest Only Period (years)</Label>
                      <Input 
                        type="number"
                        min="1"
                        max="10"
                        value={ioPeriod || ''}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 1;
                          setIoPeriod(Math.min(Math.max(val, 1), Math.min(10, loanTerm - 1)));
                        }}
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Then converts to P&I for remaining {loanTerm - ioPeriod} years
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Section - Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Repayment Amounts */}
              {results.isInterestOnly ? (
                <>
                  {/* IO Period Payments */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Interest Only Period ({results.ioPeriod} years)
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Card className="border-2 border-blue-500 bg-blue-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Weekly
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-blue-600">
                            {formatCurrency(results.weekly)}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-green-500 bg-green-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Fortnightly
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-green-600">
                            {formatCurrency(results.fortnightly)}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-purple-500 bg-purple-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Monthly
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-purple-600">
                            {formatCurrency(results.monthly)}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* P&I Period Payments */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      P&I Period (Remaining {results.piPeriod} years)
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Card className="border-2 border-orange-500 bg-orange-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Weekly
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-orange-600">
                            {formatCurrency(results.piWeekly)}
                          </div>
                          <div className="text-xs text-orange-600 mt-1">
                            +{formatCurrency(results.piWeekly - results.weekly)}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-orange-500 bg-orange-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Fortnightly
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-orange-600">
                            {formatCurrency(results.piFortnightly)}
                          </div>
                          <div className="text-xs text-orange-600 mt-1">
                            +{formatCurrency(results.piFortnightly - results.fortnightly)}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-orange-500 bg-orange-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Monthly
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-orange-600">
                            {formatCurrency(results.piMonthly)}
                          </div>
                          <div className="text-xs text-orange-600 mt-1">
                            +{formatCurrency(results.paymentIncrease)}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="border-2 border-blue-500 bg-blue-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Weekly
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatCurrency(results.weekly)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-green-500 bg-green-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Fortnightly
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-600">
                        {formatCurrency(results.fortnightly)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-purple-500 bg-purple-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Monthly
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-purple-600">
                        {formatCurrency(results.monthly)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Payment Breakdown */}
              {results.isInterestOnly ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>Interest Only Period Breakdown</CardTitle>
                      <CardDescription>First {results.ioPeriod} years - Interest only payments</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b">
                          <span className="text-gray-700 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-red-500" />
                            Interest Portion
                          </span>
                          <span className="text-xl font-bold text-red-600">
                            {formatCurrency(results.firstMonthInterest)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b">
                          <span className="text-gray-700 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                            Principal Portion
                          </span>
                          <span className="text-xl font-bold text-gray-400">
                            $0
                          </span>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                          <p className="text-sm text-orange-900">
                            During the IO period, you only pay interest. Your loan balance remains at {formatCurrency(loanAmount)}.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-orange-500">
                    <CardHeader>
                      <CardTitle>P&I Period Breakdown</CardTitle>
                      <CardDescription>After year {results.ioPeriod} - Principal & Interest payments</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b">
                          <span className="text-gray-700 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-red-500" />
                            Interest Portion
                          </span>
                          <span className="text-xl font-bold text-red-600">
                            {formatCurrency(results.firstPIInterest)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b">
                          <span className="text-gray-700 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-green-500" />
                            Principal Portion
                          </span>
                          <span className="text-xl font-bold text-green-600">
                            {formatCurrency(results.firstPIPrincipal)}
                          </span>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Payment Increase:</span>
                            <span className="text-lg font-bold text-orange-600">
                              +{formatCurrency(results.paymentIncrease)}/month
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>First Payment Breakdown</CardTitle>
                    <CardDescription>How your first monthly payment is split</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-gray-700 flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-red-500" />
                          Interest Portion
                        </span>
                        <span className="text-xl font-bold text-red-600">
                          {formatCurrency(results.firstMonthInterest)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-gray-700 flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-500" />
                          Principal Portion
                        </span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(results.firstMonthPrincipal)}
                        </span>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">Principal Paid in First Year:</span>
                          <span className="text-lg font-bold text-blue-600">
                            {formatCurrency(results.principalPaidFirstYear)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Loan Summary */}
              <Card className={loanType === 'PI' ? 'border-2 border-green-500' : 'border-2 border-orange-500'}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Loan Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-700">Loan Amount:</span>
                      <span className="font-semibold">{formatCurrency(loanAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-700">Total Interest Paid:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(results.totalInterest)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-700">Total Amount Paid:</span>
                      <span className="font-semibold">{formatCurrency(results.totalPayments)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-gray-50 px-4 rounded-lg">
                      <span className="font-bold text-gray-900">Loan Term:</span>
                      <span className="font-bold text-lg">{loanTerm} years</span>
                    </div>
                    {loanType === 'IO' && (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                        <div className="flex items-start gap-2">
                          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-900">
                            <strong>Note:</strong> After {results.ioPeriod} years of interest only payments, your loan converts to P&I for the remaining {results.piPeriod} years. Your payment will increase by {formatCurrency(results.paymentIncrease)} per month, but you'll be paying off the principal.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Information Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Principal & Interest (P&I)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  <strong>How it works:</strong> Each repayment includes both principal and interest. Over time, you pay off the loan amount.
                </p>
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                  <p className="text-sm font-semibold text-green-900 mb-2">Advantages:</p>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Build equity in your property</li>
                    <li>Lower total interest paid</li>
                    <li>Loan is fully paid off at term end</li>
                    <li>Generally lower interest rates</li>
                  </ul>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                  <p className="text-sm font-semibold text-red-900 mb-2">Disadvantages:</p>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Higher monthly repayments</li>
                    <li>Less cash flow flexibility</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Interest Only (IO)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-700">
                  <strong>How it works:</strong> You only pay the interest charges. The principal remains unchanged throughout the loan term.
                </p>
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                  <p className="text-sm font-semibold text-green-900 mb-2">Advantages:</p>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Lower monthly repayments</li>
                    <li>Better cash flow for investors</li>
                    <li>Tax deductible for investment properties</li>
                    <li>Flexibility to invest elsewhere</li>
                  </ul>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                  <p className="text-sm font-semibold text-red-900 mb-2">Disadvantages:</p>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Don't build equity</li>
                    <li>Higher total interest paid</li>
                    <li>Principal still owed at end</li>
                    <li>Usually higher interest rates</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HomeLoanCalculator;
